import Breadcrumbs from "@/components/Breadcrumbs";
import { CommentsService, PageService } from "@/services/admin-services";
import { Page, PageComment } from "@/services/admin-services/models";
import { LocaleService } from "@/services/global-services";
import { Language, Locale } from "@/services/global-services/models";
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Divider,
    FormControlLabel,
    FormGroup,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    alpha,
    useTheme,
} from "@mui/material";
import { groupBy } from "lodash";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

const breadcrumbs = [
    {
        to: "..",
        title: "Administration",
    },
    {
        title: "Page Comments",
        to: "",
    },
];

export interface ElementInfo {
    slug: string;
    name: string;
}

const Overview = () => {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [comments, setComments] = useState<PageComment[]>([]);
    const [languages, setLanguages] = useState<Array<Language>>([]);
    const [pages, setPages] = useState<Array<Page>>([]);
    const [filterRequireApproval, setFilterRequireApproval] = useState<boolean>(true);

    const navigate = useNavigate();
    const theme = useTheme();

    const commentsService = useMemo(() => new CommentsService("int"), []);
    const pagesService = useMemo(() => new PageService("int"), []);

    useEffect(() => {
        Promise.all([
            commentsService.getPageComments().then((comments) => {
                setComments(comments);
            }),
            LocaleService.get().then(parseLanguages),
            pagesService.getPages().then(setPages),
        ]).then(() => setIsLoading(false));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const grouped = useMemo(
        () =>
            isLoading
                ? {}
                : groupBy(
                      (filterRequireApproval ? comments.filter((x) => x.unreadComments > 0) : comments).sort((a, b) => {
                          return a.slug.localeCompare(b.slug);
                      }),
                      (x) => x.slug
                  ),
        [comments, filterRequireApproval, isLoading]
    );

    const parseLanguages = (regions: Array<Locale>) => {
        const languages: Array<Language> = [];
        for (let i = 0; i < regions.length; i++) {
            const region = regions[i];

            for (let j = 0; j < region.languages.length; j++) {
                const language = region.languages[j];
                if (languages.findIndex((x) => x.label === language.label) > -1) continue;

                const split = language.slug.split("-");
                languages.push({
                    ...language,
                    slug: `int-${split[1]}`,
                    regionLabel: "International",
                });
            }
        }

        setLanguages(languages.sort((a, b) => a.label.localeCompare(b.label)));
    };

    const buildSlug = (page: Page) => {
        let result = page.slug;

        if (page.parentPageId) {
            const parent = pages.find((x) => x.id === page.parentPageId)!;
            result = `${buildSlug(parent)}/${result}`;
        }

        return result;
    };

    const getSlug = (pageSlug: string) => {
        const page = pages.find((x) => x.slug === pageSlug)!;
        return buildSlug(page);
    };

    const renderLanguageDetails = (item: PageComment) => {
        return (
            <>
                <TableCell component="td" scope="row">
                    <Typography color={"text.secondary"} variant={"subtitle2"}>
                        {languages.find((x) => x.slug === item.languageCode)?.label || "-"}
                    </Typography>
                </TableCell>
                <TableCell component="td" scope="row">
                    <Typography
                        color={"text.secondary"}
                        variant={"subtitle2"}
                        sx={{ display: "flex", alignItems: "center" }}
                    >
                        {item.totalComments}{" "}
                        {item.unreadComments > 0 && (
                            <Typography
                                variant={"caption"}
                                fontWeight={700}
                                sx={{
                                    bgcolor: alpha(theme.palette.warning.light, 0.1),
                                    color: theme.palette.warning.dark,
                                    paddingX: 1.5,
                                    paddingY: 0.5,
                                    borderRadius: 4,
                                    display: "inline",
                                    ml: 3,
                                }}
                            >
                                {`${item.unreadComments} unread`}
                            </Typography>
                        )}
                    </Typography>
                </TableCell>
                <TableCell scope="row" sx={{ p: 0 }}>
                    <Box sx={{ display: "flex" }}>
                        <Button
                            color={"primary"}
                            sx={{ ml: "auto", mr: 1 }}
                            onClick={() => navigate(`/admin/page-comments/${item.slug}/${item.languageCode}`)}
                        >
                            Manage comments
                        </Button>
                        <Button
                            component={"a"}
                            color={"secondary"}
                            sx={{ ml: 1, mr: 1 }}
                            target="_blank"
                            href={`/${item.languageCode.replace("int", "be")}/${getSlug(item.slug)}`}
                        >
                            View on element page
                        </Button>
                    </Box>
                </TableCell>
            </>
        );
    };

    return (
        <Box>
            <Breadcrumbs trail={breadcrumbs} />

            <Divider />

            <Grid container spacing={4} p={4}>
                <Grid size={{ xs: 12, md: 12 }}>
                    <FormGroup sx={{ alignContent: "flex-end" }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={filterRequireApproval}
                                    onChange={(e) => setFilterRequireApproval(e.target.checked)}
                                />
                            }
                            label={<Typography variant="body2">Only show unread comments</Typography>}
                            sx={{ mr: 0 }}
                        />
                    </FormGroup>
                </Grid>
                <Grid size={{ xs: 12, md: 12 }}>
                    <TableContainer component={Paper}>
                        <Table sx={{ minWidth: 750 }} aria-label="simple table">
                            <TableHead sx={{ bgcolor: "alternate.main" }}>
                                <TableRow>
                                    <TableCell>
                                        <Typography
                                            color={"text.secondary"}
                                            variant={"caption"}
                                            fontWeight={700}
                                            sx={{ textTransform: "uppercase" }}
                                        >
                                            Page
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            color={"text.secondary"}
                                            variant={"caption"}
                                            fontWeight={700}
                                            sx={{ textTransform: "uppercase" }}
                                        >
                                            Language
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            color={"text.secondary"}
                                            variant={"caption"}
                                            fontWeight={700}
                                            sx={{ textTransform: "uppercase" }}
                                        >
                                            Comments
                                        </Typography>
                                    </TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={9}
                                            sx={{
                                                height: 400,
                                                alignItems: "center",
                                                flexDirection: "column",
                                            }}
                                        >
                                            <Box sx={{ display: "flex", placeContent: "center" }}>
                                                <CircularProgress />
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    Object.keys(grouped).map((key, i) => {
                                        const rows = grouped[key];

                                        return (
                                            <Fragment key={i}>
                                                <TableRow sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                                                    <TableCell
                                                        component="th"
                                                        scope="row"
                                                        rowSpan={rows.length}
                                                        sx={{ verticalAlign: "top" }}
                                                    >
                                                        <Typography color={"text.secondary"} variant={"subtitle2"}>
                                                            {pages.find((x) => x.slug === key)?.title ?? key}
                                                        </Typography>
                                                        <Typography color={"text.secondary"} variant={"caption"}>
                                                            /{getSlug(key)}
                                                        </Typography>
                                                    </TableCell>
                                                    {renderLanguageDetails(rows[0])}
                                                </TableRow>
                                                {rows.slice(1).map((row, i) => (
                                                    <TableRow key={i}>{renderLanguageDetails(row)}</TableRow>
                                                ))}
                                            </Fragment>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
            </Grid>
        </Box>
    );
};

export default Overview;
